import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";

const UNREAD_KEY = ["notifications", "unread-count"] as const;
const LIST_KEY = ["notifications", "list"] as const;

/**
 * Backend types — mirror worker/routes/notifications.js response shape.
 * Timestamps come across the wire as ISO strings (drizzle timestamp mode →
 * Date → JSON.stringify → ISO). readAt is null when unread.
 */
export type NotificationType =
  | "auto_delete_warning"
  | "approval_change"
  | "video_uploaded"
  | "mention"
  | "comment_reply"
  | "new_comment"
  | "comment_mention" // legacy — pre-2026-08-22 rows
  | string; // future-proof for new types

export type NotificationRow = {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
};

/**
 * GET /api/notifications/unread-count — powers the sidebar bell badge.
 * Workspace-role gated (worker/routes/notifications.js). Superadmin has no
 * notifications, so this hook should only be mounted inside /app or /admin.
 *
 * Returns 0 (not an error) on 401/403 so a logged-out flash doesn't throw —
 * RequireAuth/RequireRole already gate the routes that render the rail.
 */
export function useUnreadNotificationCount() {
  return useQuery<number>({
    queryKey: UNREAD_KEY,
    queryFn: async () => {
      try {
        const res = await api.get<{ unread: number }>("/notifications/unread-count");
        return res.unread;
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          return 0;
        }
        throw err;
      }
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  });
}

/**
 * GET /api/notifications — newest-first feed. `onlyUnread` toggles server-side
 * filter (via ?onlyUnread=true). Default limit 50 matches the server default.
 */
export function useNotifications(opts: { onlyUnread?: boolean; limit?: number } = {}) {
  const onlyUnread = !!opts.onlyUnread;
  const limit = opts.limit ?? 50;
  return useQuery<NotificationRow[]>({
    queryKey: [...LIST_KEY, { onlyUnread, limit }],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (onlyUnread) qs.set("onlyUnread", "true");
      qs.set("limit", String(limit));
      return api.get<NotificationRow[]>(`/notifications?${qs.toString()}`);
    },
    staleTime: 15_000,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: UNREAD_KEY });
  qc.invalidateQueries({ queryKey: LIST_KEY });
}

/** PATCH /api/notifications/:id/read */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<{ ok: true; id: string }>(`/notifications/${id}/read`),
    onSuccess: () => invalidateAll(qc),
  });
}

/** PATCH /api/notifications/read-all */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.patch<{ ok: true }>("/notifications/read-all"),
    onSuccess: () => invalidateAll(qc),
  });
}

/** DELETE /api/notifications/:id — permanent dismiss */
export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: true; id: string; dismissed: true }>(`/notifications/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}
